"use client";

import { useI18n } from "@/i18n/client";
import { useCartContext } from "@/providers/CartContextProvider";
import { FC, useEffect } from "react";
import styles from "./Cart.module.scss";
import { CartProducts } from "./CartProducts/CartProducts";
import { CartSummary } from "./CartSummary/CartSummary";
import { CartBuilderResponseType } from "./CartTypes";
import { EmptyCart } from "./EmptyCart/EmptyCart";
import { FreeShipping } from "./FreeShipping/FreeShipping";
import { OrderHelp } from "./OrderHelp/OrderHelp";

import {
	DataLayerProductData,
	useDataLayerContext,
} from "@/datalayer/DataLayerContextProvider";
import useRegionAndLocale from "@/hooks/useRegionAndLocale";
import DiscountBanner from "../UI/DiscountBanner/DiscountBanner";

interface Props {
	creditInfo:
		| {
				enabled: boolean;
				amount: number;
		  }
		| undefined;
	builderContent: CartBuilderResponseType;
}

const Cart: FC<Props> = ({ creditInfo, builderContent }) => {
	const { region } = useRegionAndLocale();

	const { cart, cartProducts, blendedItems, totalItems, refreshCart } =
		useCartContext();

	const { viewCart } = useDataLayerContext();

	const t = useI18n();

	useEffect(() => {
		// Refresh cart on mount to ensure latest data including discounts
		refreshCart();
	}, []); // Empty dependency array - only run once on mount

	useEffect(() => {
		if (cart) {
			const lineItemMap = new Map(
				cart.line_items.physical_items.map((item) => [
					item.product_id,
					item.quantity,
				]),
			);

			if (!cartProducts) return;
			const dlpsArray: DataLayerProductData[] = cartProducts.map((product) => ({
				product,
				quantity: lineItemMap.get(product.id) ?? 1,
				variantId: product.productId,
			}));
			viewCart(cart, dlpsArray);
		}
	}, [cart, cartProducts, viewCart]);

	if (!cart || totalItems === 0) {
		return <EmptyCart />;
	}

	return (
		<section className={styles.container}>
			<DiscountBanner messageSize='LONG' store={region} />
			<h1 className={styles.cartTitle}>
				{t("Cart.myCart")} {!!totalItems && `(${totalItems})`}
			</h1>

			<div className={styles.cart}>
				<CartProducts
					blendedItems={blendedItems ?? []}
					searchSpringId={builderContent.productRecommendations}
				/>

				<div className={styles.checkout}>
					<FreeShipping
						cartTotal={cart.base_amount}
						freeShippingNotification={builderContent.freeDeliveryNotification}
					/>
					<CartSummary
						creditInfo={creditInfo}
						shippingSettings={builderContent.shippingSettings}
						cartSummary={builderContent.cartSummary}
						store={region}
					/>
					<OrderHelp
						supportingInformation={builderContent.supportingInformation}
					/>
				</div>
			</div>
		</section>
	);
};

export default Cart;
